import {
  createAction,
  createActionGroup,
  emptyProps,
  props,
} from '@ngrx/store';
import {
  CustomerRequest,
  CustomerResponse,
  PriceRequest,
  PriceResponse,
  SetupIntentRequest,
  SetupIntentResponse,
  SubscriptionRequest,
  SubscriptionResponse,
  InvoiceRequest,
  InvoiceResponse,
  DonationMetadata,
  StripeInvoiceLite,
} from '../../types/stripe-models';
import {
  CardFeeType,
  DonationType,
  FeeCalculationStatus,
  FeeReview,
  FlowMode,
  PriceDraft,
} from '../../types/donation-flow.models';

/** UI (incluye triggers de los flujos separados) */
export const DonationFlowUiActions = createActionGroup({
  source: 'DonationFlow/UI',
  events: {
    Reset: emptyProps(),
    'Set Step': props<{ step: number }>(),
    'Set Donation Type': props<{ donationType: DonationType }>(),
    /**
     * Central switch: dispatch this instead of `setDonationType` whenever
     * the user picks a new type in step 1. Reducer/effects use it to clean
     * every piece of state that belongs to the previous flow (setupIntent,
     * paymentMethodId, cardFeeType, feeReview, invoice, subscription, ...).
     */
    'Change Donation Type': props<{ donationType: DonationType }>(),
    'Set Flow Mode': props<{ flowMode: FlowMode; source: 'auto' | 'manual' }>(),
    'Set Donation Metadata': props<{ metadata: DonationMetadata | null }>(),
    'Set Price Draft': props<{ draft: PriceDraft | null }>(),

    'Toggle Cover Fees': props<{ coverFees: boolean }>(),
    'Set Card Fee Type': props<{ cardFeeType: CardFeeType }>(),
    'Set Fee Calculation Status': props<{ status: FeeCalculationStatus }>(),
    'Reset Card Brand State': emptyProps(),

    'Set Defer First Charge': props<{ isFirstChargeDeferred: boolean }>(),
     'Set First Payment Date': props<{ firstPaymentDate: string | null }>(),
    /** Triggers de Step 2 por flujo */
    // ONE-TIME ahora: Customer + Price (parallel) -> SetupIntent(Create) -> (client confirm) -> Invoice
    'Begin Step2 OneTime': props<{ customer: CustomerRequest }>(),

    // RECURRING: Customer + Price (parallel) -> SetupIntent(Create) -> (client confirm) -> Subscription
    'Begin Step2 Recurring': props<{ customer: CustomerRequest }>(),

    /** Confirmaciones de SetupIntent (separadas por tipo para mayor claridad) */
    'Confirm OneTime SetupIntent': props<{
      setupIntentId: string;
      paymentMethodId: string;
    }>(),
    'Confirm Recurring SetupIntent': props<{
      setupIntentId: string;
      paymentMethodId: string;
    }>(),

    /** Hidratar estado tras la confirmación en cliente (Payment Element) */
    'Hydrate SetupIntent After Client Confirm': props<{
      setupIntent: SetupIntentResponse;
    }>(),
  },
});

/** Comandos Stripe (sólo para setear loading y registrar resultados) */
export const DonationFlowStripeActions = createActionGroup({
  source: 'DonationFlow/Stripe',
  events: {
    // Customer
    'Send Customer': props<{ request: CustomerRequest }>(),
    'Send Customer Success': props<{ response: CustomerResponse }>(),
    'Send Customer Failure': props<{ error: unknown }>(),

    // Price
    'Send Price': props<{ request: PriceRequest }>(),
    'Send Price Success': props<{ response: PriceResponse }>(),
    'Send Price Failure': props<{ error: unknown }>(),

    // SetupIntent (se usa en ambos flujos)
    'Send SetupIntent': props<{ request: SetupIntentRequest }>(),
    'Send SetupIntent Success': props<{ response: SetupIntentResponse }>(),
    'Send SetupIntent Failure': props<{ error: unknown }>(),

    // Subscription (recurring)
    'Send Subscription': props<{ request: SubscriptionRequest }>(),
    'Send Subscription Success': props<{ response: SubscriptionResponse }>(),
    'Send Subscription Failure': props<{ error: unknown }>(),

    // Invoice (one-time con auto-charge al finalizar)
    'Send Invoice': props<{ request: InvoiceRequest }>(),
    'Send Invoice Success': props<{ response: StripeInvoiceLite }>(),
    'Send Invoice Failure': props<{ error: unknown }>(),
  },
});

/** Helpers */
export const DonationFlowHelpersActions = createActionGroup({
  source: 'DonationFlow/Helpers',
  events: {
    'Set Payment Method Id From Confirm': props<{
      paymentMethodId: string;
      setupIntentId: string;
      status?: string;
    }>(),

    // Suscription helpers (recurring)
    'Confirm Subscription First Invoice Payment': props<{
      clientSecret: string;
      subscriptionId: string;
    }>(),
    'Confirm Invoice Payment Success': props<{ paymentIntent: any }>(),
    'Confirm Invoice Payment Failure': props<{ error: unknown }>(),

    'Retrieve Subscription': props<{ id: string }>(),
    'Retrieve Subscription Success': props<{
      response: SubscriptionResponse;
    }>(),
    'Retrieve Subscription Failure': props<{ error: unknown }>(),

    'Finalize Recurring Success': props<{ subscriptionId: string }>(),
  },
});

export const showFeeReview = createAction(
  '[DonationFlow] Show Fee Review',
  props<FeeReview>()
);

export const hideFeeReview = createAction(
  '[DonationFlow] Hide Fee Review'
);

export const acceptFeeReview = createAction(
  '[DonationFlow] Accept Fee Review'
);

export const useAnotherCard = createAction(
  '[DonationFlow] Use Another Card'
);

export const cancelFeeReview = createAction(
  '[DonationFlow] Cancel Fee Review'
);
